import './index.scss'
import React from 'react'
import connect from './connect'
import moment from 'moment'
import { Button, Icon, Switcher } from '@ui'
import { t } from 'i18next'

class DaySelect extends React.Component {
  toggleModeEnabled = false

  constructor (props) {
    super(props)

    this.state = {
      selectingPeriod: false, // Toggles the mode
      period: {
        start: null,
        end: null
      },
      selected: [],
      displayedMonth: (this.props.value && moment(this.props.value).isValid())
        ? moment(this.props.value).format('YYYY-MM')
        : (this.props.values && this.props.values.length && moment(this.props.values[0]).isValid())
          ? moment(this.props.values[0]).format('YYYY-MM')
          : (this.props.calendar && this.props.calendar.date)
            ? moment(this.props.calendar.date).format('YYYY-MM')
            : moment().format('YYYY-MM')
    }

    if (this.props.toggleModeEnabled) this.toggleModeEnabled = this.props.toggleModeEnabled
  }

  componentDidMount () {
    if (this.props.value) { // single value passed
      this.setState((s) => Object.assign({}, s, { selected: [moment(this.props.value).format('YYYY-MM-DD')] }))
    } else if (this.props.values) { // list of values passed
      this.setState((s) => Object.assign({}, s, { selected: this.props.values.map(v => moment(v).format('YYYY-MM-DD')) }))
    }

    if (this.props.executeOnMonthSelect) this.props.executeOnMonthSelect(moment(this.state.displayedMonth).format('YYYY-MM-DD'))
  }

  getCalRows () {
    const rows = []
    let i = 0
    let forward = moment(this.state.displayedMonth).add(i, 'weeks').startOf('week')
    let back = moment(this.state.displayedMonth).add(-i, 'weeks').endOf('week')

    const { start, end } = this.state.period
    const { selectingPeriod, selected } = this.state

    // Helper to process day properties
    const createDayObj = (dayMoment) => {
      const dayStr = dayMoment.format('YYYY-MM-DD')

      let isPeriodStart = false
      let isPeriodEnd = false
      let isInPeriod = false

      if (selectingPeriod && start) {
        if (dayStr === start) isPeriodStart = true
        if (end && dayStr === end) isPeriodEnd = true

        // Check if between start and end (inclusive)
        if (start && end) {
          if (dayMoment.isBetween(start, end, 'day', '[]')) {
            isInPeriod = true
          }
        } else if (start && !end) {
          // If only start is selected, highlight just that day
          if (dayStr === start) isInPeriod = true
        }
      }

      return {
        day: dayMoment,
        isSelected: !!selected.find(s => s === dayStr), // Original selection logic
        isPeriodStart,
        isPeriodEnd,
        isInPeriod,
        thisMonth: dayMoment.format('YYYY-MM') === this.state.displayedMonth,
        isToday: dayStr === moment().format('YYYY-MM-DD')
      }
    }

    while (forward.format('YYYY-MM') === this.state.displayedMonth || back.format('YYYY-MM') === this.state.displayedMonth) {
      const rowBack = []
      const rowForward = []
      for (var j = 0; j < 7; j++) {
        const db = back.clone().startOf('week').add(j, 'days')
        const df = forward.clone().startOf('week').add(j, 'days')

        rowBack.push(createDayObj(db))
        rowForward.push(createDayObj(df))
      }

      if (back.format('YYYY-MM') === this.state.displayedMonth) {
        rows.unshift({ days: rowBack, date: back.clone().startOf('week') })
      }
      if (forward.format('YYYY-MM') === this.state.displayedMonth) {
        rows.push({ days: rowForward, date: forward.clone().startOf('week') })
      }

      i += 1
      forward = moment(this.state.displayedMonth).add(i, 'weeks').startOf('week')
      back = moment(this.state.displayedMonth).add(-i, 'weeks').endOf('week')
    }

    // make sure we don't have duplicate weeks in the list
    if (rows.length >= 2 && rows[0].date.format('YYYY-MM-DD') === rows[1].date.format('YYYY-MM-DD')) {
      rows.shift()
    }

    return rows
  }

  toggleDaySelection (dayMoment) {
    // prevent selecting past weeks if props.preventPast
    if (this.props.forbiddenPast && dayMoment.startOf('day').isBefore(moment().startOf('day'))) return

    if (this.state.selectingPeriod) {
      this.handlePeriodSelection(dayMoment)
    } else {
      this.handleStandardSelection(dayMoment)
    }
  }

  handlePeriodSelection (dayMoment) {
    const dayStr = dayMoment.format('YYYY-MM-DD')
    const { start, end } = this.state.period

    let newPeriod = { start: null, end: null }
    let newSelected = []

    if (!start) {
      // 1. First click: Set Start
      newPeriod = { start: dayStr, end: null }
      newSelected = [dayStr]
    } else if (start && !end) {
      // 2. Second click: Determine if it's a new Start or an End
      if (dayMoment.isBefore(start)) {
        // User clicked before the current start -> Reset and make this the new start
        newPeriod = { start: dayStr, end: null }
        newSelected = [dayStr]
      } else {
        // User clicked after start -> Valid Period
        newPeriod = { start: start, end: dayStr }

        // --- KEY CHANGE: Generate all days in between ---
        const curr = moment(start)
        const stop = moment(dayStr)

        while (curr.diff(stop) <= 0) {
          newSelected.push(curr.format('YYYY-MM-DD'))
          curr.add(1, 'days')
        }
      }
    } else {
      // 3. Third click (Period already full): Reset and start new period
      newPeriod = { start: dayStr, end: null }
      newSelected = [dayStr]
    }

    // Update state
    this.setState({
      period: newPeriod,
      selected: newSelected
    })

    // Trigger callbacks
    // Standard onChange gets the full array of days
    if (this.props.onChange) this.props.onChange(newSelected)

    // Special Period callback gets the start/end object
    if (this.props.onPeriodChange && newPeriod.start && newPeriod.end) {
      this.props.onPeriodChange(newPeriod)
    }
  }

  handleStandardSelection (dayMoment) {
    const curr = this.state.selected.find(s => s === dayMoment.format('YYYY-MM-DD'))
    let newSel = this.state.selected
    if (curr) {
      if (!this.props.singleDaySelection) newSel = newSel.filter(s => s !== curr)
    } else {
      if (!this.props.singleDaySelection) {
        newSel.push(dayMoment.format('YYYY-MM-DD'))
      } else {
        newSel = [dayMoment.format('YYYY-MM-DD')]
      }
    }
    this.setState((s) => Object.assign({}, s, { selected: newSel }))

    if (this.props.onChange) this.props.onChange(newSel)
  }

  toggleMode (isPeriodMode) {
    // Reset selections when switching modes to avoid confusion
    this.setState({
      selectingPeriod: isPeriodMode
      // period: { start: null, end: null },
      // selected: []
    })

    // if (this.props.onChange) this.props.onChange([])
  }

  render () {
    const { className } = this.props
    const cN = ['ds-day-select']
    if (className) cN.push(className)
    const rows = this.getCalRows()
    let titleTxt = moment(this.state.displayedMonth).format('MMMM YYYY')
    titleTxt = titleTxt.charAt(0).toUpperCase() + titleTxt.slice(1)

    return (
      <div className={cN.join(' ')}>
        {/* Mode Switcher UI */}
        {this.toggleModeEnabled
          ? (
            <div className='ds-mode-switch' style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem', gap: '1rem', alignItems: 'center' }}>
              <Switcher
                size={Switcher.SIZES.COMPACT}
                value={this.state.selectingPeriod}
                onSelect={(v) => this.setState({
                  selectingPeriod: v
                })}
                options={[
                  {
                    label: t('DAYS'),
                    value: false
                  },
                  {
                    label: t('PERIOD'),
                    value: true
                  }
                ]}
              />

              <Button
                color='RED'
                disabled={this.state.selected.length === 0}
                onClick={() => {
                  this.setState({ selected: [] })
                  if (this.props.onChange) this.props.onChange([])
                }}
                style='BORDERLESS'
                ico='delete'
              >
                {t('DELETE_SELECTED')}
              </Button>
            </div>
          )
          : null}

        <div className='ds-day-select-controls'>
          <Button
            style={Button.STYLES.LIGHT}
            ico={Icon.ICONS.arrowLeft}
            onClick={(e) => {
              const newMonth = moment(this.state.displayedMonth, 'YYYY-MM').add(-1, 'month')
              this.setState((s) => Object.assign({}, s, { displayedMonth: newMonth.format('YYYY-MM') }))

              if (this.props.executeOnMonthSelect) this.props.executeOnMonthSelect(newMonth.format('YYYY-MM-DD'))
              e.stopPropagation()
            }}
          />
          <div className='ds-title'>{titleTxt}</div>
          <Button
            style={Button.STYLES.LIGHT}
            ico={Icon.ICONS.arrowRight}
            onClick={(e) => {
              const newMonth = moment(this.state.displayedMonth, 'YYYY-MM').add(1, 'month')
              this.setState((s) => Object.assign({}, s, { displayedMonth: newMonth.format('YYYY-MM') }))

              if (this.props.executeOnMonthSelect) this.props.executeOnMonthSelect(newMonth.format('YYYY-MM-DD'))
              e.stopPropagation()
            }}
          />
        </div>

        <div className='ds-day-select-cal-weekdays'>
          <div className='ds-day-select-cal-weekdays-day'>{moment().startOf('week').add(0, 'days').format('ddd')}</div>
          <div className='ds-day-select-cal-weekdays-day'>{moment().startOf('week').add(1, 'days').format('ddd')}</div>
          <div className='ds-day-select-cal-weekdays-day'>{moment().startOf('week').add(2, 'days').format('ddd')}</div>
          <div className='ds-day-select-cal-weekdays-day'>{moment().startOf('week').add(3, 'days').format('ddd')}</div>
          <div className='ds-day-select-cal-weekdays-day'>{moment().startOf('week').add(4, 'days').format('ddd')}</div>
          <div className='ds-day-select-cal-weekdays-day'>{moment().startOf('week').add(5, 'days').format('ddd')}</div>
          <div className='ds-day-select-cal-weekdays-day'>{moment().startOf('week').add(6, 'days').format('ddd')}</div>
        </div>

        {rows.map((r, iw) =>
          <div
            key={'week_' + iw.toString()}
            className={['ds-day-select-cal-week', this.state.selected === r.date.format('YYYY-MM-DD') ? 'selected-week' : null].join(' ')}
          >
            {r.days.map((d, id) =>
              <div
                onClick={(e) => {
                  this.toggleDaySelection(d.day)
                  if (!this.props.singleDaySelection) e.stopPropagation()
                }}
                key={'day_' + iw.toString() + '_' + id.toString()}
                className={[
                  'ds-day-select-cal-day',
                  d.thisMonth ? null : 'other-month',
                  d.isToday ? 'today' : null,
                  // Standard Selection Class
                  d.isSelected ? 'is-selected' : null,
                  // Period Selection Classes
                  d.isInPeriod ? 'is-selected' : null,
                  // d.isPeriodStart ? 'period-start' : null,
                  // d.isPeriodEnd ? 'period-end' : null,
                  this.props.forbiddenPast && d.day.startOf('day').isBefore(moment().startOf('day')) ? 'is-past-forbidden' : null,
                  'date_' + moment(d.day).format('YYYY-MM-DD')
                ].join(' ')}
              >
                {moment(d.day).format('D')}
              </div>
            )}
          </div>
        )}
      </div>
    )
  }
}

export default connect(DaySelect)
